import { api_fetch } from "../../../api/client"

// Forecast data in and out (server: `server/src/forecast_io.rs`): bulk import with its
// template, seeding a year from the prior year's actuals, the NetSuite price suggestion for
// a customer × item, and the budget-vs-actual rollup.

const API_BASE = "/forecast-versions"

export interface ImportRefusal {
  line: number
  reason: string
}

/** One leaf × period an import or seed will write as a forecast row. */
export interface PlannedCell {
  line: number
  customer: string
  product_line: string
  sku: string
  period: string
  path_key: string
  creates_node: boolean
  replaces_existing: boolean
  store_count: number
  cases: string
  volume_dollars: string
  case_cost: string
}

export interface ImportPreviewResponse {
  file_name: string
  rows_parsed: number
  rows_matched: number
  rows_creating_nodes: number
  new_node_count: number
  refused: ImportRefusal[]
  cells: PlannedCell[]
  applied: boolean
  updated_at: string | null
}

export interface SeedFromActualsRequest {
  source_version_id: string | null
  from_year: number
  to_year: number
  overwrite: boolean
  dry_run: boolean
}

export interface SeedPreviewResponse {
  source_version_id: string
  source_version_name: string
  from_year: number
  to_year: number
  source_actual_rows: number
  rows_to_create: number
  rows_to_replace: number
  rows_skipped_existing: number
  new_node_count: number
  total_dollars: string
  cells: PlannedCell[]
  applied: boolean
  updated_at: string | null
}

export interface SuggestedPrice {
  /** One forecast case: `price_as_listed × per_case_multiplier`. */
  price_per_case: string | null
  source: "customer price level" | "price list" | "item list price" | null
  label: string | null
  customer_name: string | null
  /** The price as its source lists it (a stick, a caddy) — absent from servers before round 5. */
  price_as_listed?: string | null
  /** How many of what the source prices make a case (1 when it already prices a case). */
  per_case_multiplier?: string | null
}

export interface BudgetVsActualRow {
  customer: string
  period: string
  budget_dollars: string
  budget_cases: string
  actual_dollars: string
  actual_cases: string
  budget_cogs: string | null
  actual_cogs: string | null
}

export interface BudgetVsActualResponse {
  version_id: string
  budget_version_id: string
  budget_version_name: string
  periods: string[]
  rows: BudgetVsActualRow[]
  cogs_coverage_pct: number | null
}

/** Message the server put on a 4xx, else the status text. */
async function error_message(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json()
    if (body && typeof body.message === "string") return body.message
  } catch {
    // not JSON
  }
  return `${fallback}: ${response.statusText}`
}

export async function download_import_template(version_id: string): Promise<Blob> {
  const response = await fetch(`/api${API_BASE}/${version_id}/import-template`, { method: "GET", credentials: "same-origin" })
  if (!response.ok) {
    throw new Error(await error_message(response, "Could not download the template"))
  }
  return response.blob()
}

/** Upload a CSV/XLSX; `dry_run` previews without writing. A file with refused rows never writes. */
export async function import_forecast(version_id: string, file: File, dry_run: boolean): Promise<ImportPreviewResponse> {
  const form = new FormData()
  form.append("file", file, file.name)
  form.append("dry_run", dry_run ? "true" : "false")
  const response = await fetch(`/api${API_BASE}/${version_id}/import`, {
    method: "POST",
    credentials: "same-origin",
    body: form,
  })
  if (!response.ok) {
    throw new Error(await error_message(response, "Import failed"))
  }
  return (await response.json()) as ImportPreviewResponse
}

export async function seed_from_actuals(version_id: string, body: SeedFromActualsRequest): Promise<SeedPreviewResponse> {
  const result = await api_fetch<SeedPreviewResponse>(`${API_BASE}/${version_id}/seed-from-actuals`, { method: "POST", body })
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value.data
}

export async function get_suggested_price(customer: string | null, product_id: string): Promise<SuggestedPrice> {
  const result = await api_fetch<SuggestedPrice>(`/forecast/suggested-price`, {
    params: { customer: customer ?? undefined, product_id },
    use_cache: true,
  })
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value.data
}

export async function get_budget_vs_actual(version_id: string, budget_version_id: string | null): Promise<BudgetVsActualResponse> {
  const result = await api_fetch<BudgetVsActualResponse>(`${API_BASE}/${version_id}/budget-vs-actual`, {
    params: { budget_version_id: budget_version_id ?? undefined },
  })
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value.data
}
